'use client';

import { useState } from 'react';
import { cn } from '../../lib/utils';
import { Icon } from '@iconify/react';

/**
 * Tech badge component
 * Displays a technology name with its official brand logo and colors
 * Enhanced with hover effects, tooltips, and smooth animations
 * Responsive design adapts to different screen sizes
 */
export function TechBadge({
  name,
  className,
  category,
  icon,
  color
}: {
  name: string;
  className?: string;
  category?: string;
  icon?: string;
  color?: string;
}) {
  const [isHovered, setIsHovered] = useState(false);

  // Default color if none provided
  const defaultColor = '#64748b';
  const badgeColor = color || defaultColor;

  // Adaptive gradient and color functions to ensure good contrast
  const getGradient = () => {
    // Convert hex to RGB for manipulation
    const hexToRgb = (hex: string): number[] => {
      const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
      const hexColor = hex.replace(shorthandRegex, (_m, r, g, b) => r + r + g + g + b + b);
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hexColor);
      return result
        ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)]
        : [100, 116, 139]; // Default slate color
    };

    const rgbColor = hexToRgb(badgeColor);

    return {
      light: {
        bg: `linear-gradient(135deg, rgba(${rgbColor[0]}, ${rgbColor[1]}, ${rgbColor[2]}, 0.08) 0%, rgba(${rgbColor[0]}, ${rgbColor[1]}, ${rgbColor[2]}, 0.03) 100%)`,
        border: `rgba(${rgbColor[0]}, ${rgbColor[1]}, ${rgbColor[2]}, 0.15)`,
        hoverBg: `linear-gradient(135deg, rgba(${rgbColor[0]}, ${rgbColor[1]}, ${rgbColor[2]}, 0.12) 0%, rgba(${rgbColor[0]}, ${rgbColor[1]}, ${rgbColor[2]}, 0.06) 100%)`,
      },
      dark: {
        bg: 'linear-gradient(135deg, rgba(30, 41, 59, 0.7) 0%, rgba(15, 23, 42, 0.6) 100%)',
        border: 'rgba(51, 65, 85, 0.5)',
        hoverBg: 'linear-gradient(135deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.7) 100%)',
      }
    };
  };

  const styles = getGradient();

  return (
    <div
      className={cn(
        'relative inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium',
        'backdrop-blur-sm border',
        'transition-all duration-300 hover:-translate-y-0.5',
        className
      )}
      style={{
        background: isHovered
          ? `var(--tw-dark) ? ${styles.dark.hoverBg} : ${styles.light.hoverBg}`
          : `var(--tw-dark) ? ${styles.dark.bg} : ${styles.light.bg}`,
        borderColor: `var(--tw-dark) ? ${styles.dark.border} : ${styles.light.border}`,
        boxShadow: isHovered
          ? '0 2px 5px rgba(0, 0, 0, 0.05)'
          : 'none',
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Icon */}
      {icon && (
        <Icon
          icon={icon}
          className="h-3.5 w-3.5"
          style={{ color: badgeColor }}
        />
      )}

      {/* Technology name */}
      <span className="text-slate-900 dark:text-slate-200">
        {name}
      </span>

      {/* Tooltip on hover */}
      {category && isHovered && (
        <div
          className="absolute -top-9 left-1/2 transform -translate-x-1/2 px-2 py-1 bg-slate-800/90 text-white text-[10px] rounded-md shadow-lg z-10 whitespace-nowrap backdrop-blur-sm animate-in fade-in zoom-in duration-200"
        >
          {category}
          <div
            className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-slate-800/90 rotate-45"
          />
        </div>
      )}
    </div>
  );
} 